import {classList, dataset, dispatch} from 'nuffjs';
// import {getScrollTop} from '../utils/get-scroll';
import {onFrame, waitFrame} from '../utils/wait';
import {onTransitionEnd, getTransitionDuration} from '../utils/animations';

let gsap = null;

async function getGsap() {
	if (!gsap) {
		gsap = (await import('../../external/gsap')).gsap;
	}
	return gsap;
}

/**
 * Configuration values
 *
 * @property {string} collapsedClass=collapsed the class toggled on the element during collapse /
 * expand, it tipically trigger the corresponding CSS transitions.
 * @property {string} keepAttr=collapsableKeep data-attribute to be applied on children element of
 * the collapsable that we want to stay visible even when the component is collapsed
 * @property {string} beforeToggleEvent=collapsable:beforeToggle event dispatched before toggling,
 * it can be canceled to prevent the state change
 * @property {string} toggleEvent=collapsable:toggle event dispatched after the toggling
 *
 * @memberof Collapsable
 */
const conf = {
	collapsedClass: 'collapsed',
	keepAttr: 'collapsableKeep',
	beforeToggleEvent: 'collapsable:beforeToggle',
	toggleEvent: 'collapsable:toggle',
};

/**
 * Create the Collapsable class with a dynamic super class
 *
 * @mixin
 * @param {typeof HTMLElement} Base base class to be extended
 * @returns {typeof Collapsable} the Collapsable class extending the Base class
 */
const collapsableMixin = (Base) =>
	/**
	 * @classdesc Represents an area that can be visually expanded or collapsed. The behavior is
	 * controlled by other component, typically a [Toggler]{@link Toggler}.
	 *
	 * The animation can be perfomed purely by CSS transitions (the default) or by JS with GSAP
	 * library. The second case is typically needed only when the Collapsable is part of an
	 * [Accordion]{@link Accordion}, in order to sync expanding and collapsing components together
	 * with the page scroll movement.
	 *
	 * @class Collapsable
	 * @extends HTMLElement
	 * @version 1.0.0
	 * @path
	 *
	 * @emits collapsable:beforeToggle
	 * @emits collaspable:toggle
	 *
	 */
	class extends Base {
		/**
		 * Default options, overridable by data-attributes
		 *
		 * @property {boolean} removeNegativeMargins=true flag to include / exclude negative margins
		 * in the height calculation
		 * @property {boolean} useCssTransition=true control the use of CSS transition or GSAP for
		 * the animation
		 * @property {boolean} focusOnExpand=false it will not focus the element on expand by
		 * default, as [recommended by Accessibility
		 * experts]{@link https://adrianroselli.com/2020/05/disclosure-widgets.html#Order}
		 *
		 * @memberof Collapsable
		 * @static
		 */
		static defaultOpts = {
			removeNegativeMargins: true,
			useCssTransition: true,
			focusOnExpand: false,
		};

		busy = false;
		collasped = null;

		/**
		 * Initialization. It gets the initial state by checking the presence of the collapsedClass
		 * on the element
		 *
		 * @memberof Collapsable
		 * @instance
		 */
		firstConnectedCallback() {
			this.opts = dataset(this).get(this.constructor.defaultOpts);
			this.collapsed = classList(this).contains(conf.collapsedClass);
			if (!this.opts.useCssTransition) {
				// start to download to have it likely ready when the toggle happens
				getGsap();
			}
		}

		/**
		 * Collapse the element, if it is not allready collapsed
		 *
		 * @returns {Promise} resolves after the animation
		 * @async
		 *
		 * @memberof Collapsable
		 * @instance
		 */
		collapse() {
			return this.toggle(true);
		}

		/**
		 * Expand the element, if it is not already expanded
		 *
		 * @returns {Promise} resolves after the animation
		 * @async
		 *
		 * @memberof Collapsable
		 * @instance
		 */
		expand() {
			return this.toggle(false);
		}

		/**
		 * Toggle the element state (between collapsed / expanded)
		 *
		 * @param {?boolean} [value=null] force the change to a specific state, where true =
		 * collapsed
		 * @returns {Promise} Resolves when the animation finishes
		 * @async
		 *
		 * @memberof Collapsable
		 * @instance
		 */
		async toggle(value = null) {
			// if it is already performing an animation, it ignores the call
			if (this.busy) {
				return;
			}
			value = value === null ? !this.collapsed : !!value;
			// if the requested state is the same as the current one, it does not have to do anything
			if (value === this.collapsed) {
				return;
			}
			const opts = this.opts;
			// dispatch a cancelable event before starting, in order to allow other components to
			// eventually cancel the request.
			const beforeEvent = dispatch(this, conf.beforeToggleEvent, {bubbles: true, cancelable: true});
			if (beforeEvent.defaultPrevented) {
				return;
			}
			this.busy = true;
			const cl = classList(this);
			if (!opts.useCssTransition) {
				await getGsap();
			}
			await onFrame(async () => {
				// misure the height of the element before starting, because css transition cannot
				// animate a value starting from 'auto
				const currentHeight = this.getActualHeight(this, opts.removeNegativeMargins);
				this.style.height = currentHeight + 'px';
				// force the value because there are some cases in which elements needs to allow the
				// overflow to be visible when they are not animating, so in that case it cannot be
				// set in the CSS
				this.style.overflow = 'hidden';
				await waitFrame();
				if (!value) {
					// collapsed => false
					this.setAttribute('aria-hidden', 'false');
					cl.toggle(conf.collapsedClass, value);
					await waitFrame();
					const finalHeight = this.scrollHeight;
					if (opts.useCssTransition) {
						this.style.height = finalHeight + 'px';
						await onTransitionEnd(this);
					} else {
						await new Promise(async (resolve) => {
							(await getGsap()).to(this, {
								transition: 'none',
								height: finalHeight + 'px',
								duration: this.getDuration(),
								onComplete: () => resolve(),
							});
						});
					}
					this.style.removeProperty('overflow');
				} else {
					// collapsed => true;
					this.setAttribute('aria-hidden', 'true');
					let finalHeight = 0;
					const keepElements = [...this.querySelectorAll(dataset.selector(conf.keepAttr))];
					if (keepElements.length) {
						const top = keepElements[0].getBoundingClientRect().top;
						const bottom = keepElements[keepElements.length - 1].getBoundingClientRect().bottom;
						finalHeight = Math.abs(bottom - top);
					}
					await waitFrame();
					cl.toggle(conf.collapsedClass, value);
					if (opts.useCssTransition) {
						this.style.height = finalHeight + 'px';
						await onTransitionEnd(this);
					} else {
						await new Promise(async (resolve) => {
							(await getGsap()).to(this, {
								transition: 'none',
								height: finalHeight + 'px',
								duration: this.getDuration(),
								onComplete: () => resolve(),
							});
						});
					}
					await waitFrame();
				}
			});
			this.collapsed = value;
			// clear the style
			this.style.removeProperty('height');
			this.style.removeProperty('transition');
			dispatch(this, conf.toggleEvent, {bubbles: true});

			// we focus only in special cases, controlled by a flag. It would obviously require a
			// tabindex=-1 attribute to work
			if (!this.collapsed && opts.focusOnExpand) {
				this.focus({preventScroll: true});
			}
			this.busy = false;
		}

		/**
		 * Check if the element is currently busy animating
		 *
		 * @returns {boolean} true when it's busy
		 *
		 * @memberof Collapsable
		 * @instance
		 */
		isBusy() {
			return this.busy;
		}

		/**
		 * Check if the element is currently collapsed
		 *
		 * @returns {boolean} true when it's collapsed
		 *
		 * @memberof Collapsable
		 * @instance
		 */
		isCollapsed() {
			return this.collapsed;
		}

		/**
		 * Get the animation duration, typically set in the CSS
		 *
		 * @param {boolean} [seconds=true] when true the duration will be epressed in seconds,
		 * otherwise in milliseconds
		 * @returns {number} the animation duration
		 *
		 * @memberof Collapsable
		 * @instance
		 */
		getDuration(seconds = true) {
			return getTransitionDuration(this, null, true, seconds);
		}

		/**
		 * Enable the CSS transitions, in case they were disabled
		 *
		 * @memberof Collapsable
		 * @instance
		 */
		enableCssTransition() {
			this.opts.useCssTransition = true;
		}

		/**
		 * Disable the CSS transitions, a JS animation (GSAP) will be used. Typically used by the
		 * [Accordion]{@link Accordion} to sync all the animations together
		 *
		 * @memberof Collapsable
		 * @instance
		 */
		disableCssTransition() {
			this.opts.useCssTransition = false;
			getGsap();
		}

		/**
		 * Calculate the height of the element before and after toggling, taking children staying
		 * visible into account
		 *
		 * @param {?boolean} [removeNegativeMargins=null] when null, it will use the internal option flag
		 * @returns {object} contains properties currentHeight and newHeight (numbers)
		 *
		 * @memberof Collapsable
		 * @instance
		 */
		getHeightValues(removeNegativeMargins = null) {
			if (removeNegativeMargins === null) {
				removeNegativeMargins = this.opts.removeNegativeMargins;
			}
			const currentHeight = this.getActualHeight(this, removeNegativeMargins);
			const children = this.querySelectorAll(
				':scope > *:not(' + dataset.selector(conf.keepAttr) + ')'
			);
			let contentHeight = 0;
			for (const child of children) {
				contentHeight += this.getActualHeight(child, removeNegativeMargins);
			}
			const newHeight = Math.max(0, currentHeight - contentHeight);
			return {currentHeight: currentHeight, newHeight: newHeight};
		}

		/**
		 * Calculate the current height of an element, possibly taking into account negative margins
		 *
		 * @param {HTMLElement} element the element for which we need to know the current height
		 * @param {boolean} removeNegativeMargins take into account or ignore the negative margins
		 * set in the CSS
		 * @returns {number} the calculated height
		 *
		 * @memberof Collapsable
		 * @instance
		 * @protected
		 * @ignore
		 */
		getActualHeight(element, removeNegativeMargins) {
			let height = element.getBoundingClientRect().height;
			if (removeNegativeMargins) {
				const style = getComputedStyle(element);
				const topMargin = parseFloat(style.marginTop);
				if (!isNaN(topMargin) && topMargin < 0) {
					height += topMargin;
				}
				const bottomMargin = parseFloat(style.marginBottom);
				if (!isNaN(bottomMargin) && bottomMargin < 0) {
					height += bottomMargin;
				}
			}
			return height;
		}
	};

export {collapsableMixin};
